import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "space-y-4" }
const _hoisted_2 = { class: "flex flex-wrap items-center gap-3" }
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = {
  key: 0,
  class: "rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
}
const _hoisted_5 = { class: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" }
const _hoisted_6 = { class: "overflow-x-auto" }
const _hoisted_7 = { class: "min-w-full text-sm" }
const _hoisted_8 = { class: "divide-y divide-slate-100 bg-white" }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "px-4 py-3 text-slate-500" }
const _hoisted_11 = { class: "whitespace-nowrap px-4 py-3 text-slate-700" }
const _hoisted_12 = { class: "px-4 py-3" }
const _hoisted_13 = { class: "font-medium text-slate-900" }
const _hoisted_14 = {
  key: 0,
  class: "text-xs text-slate-500"
}
const _hoisted_15 = {
  key: 1,
  class: "text-xs text-slate-500"
}
const _hoisted_16 = { class: "px-4 py-3 text-slate-700" }
const _hoisted_17 = { class: "px-4 py-3 text-slate-700" }
const _hoisted_18 = { class: "whitespace-nowrap px-4 py-3 text-right font-medium text-slate-900" }
const _hoisted_19 = { class: "px-4 py-3" }
const _hoisted_20 = {
  key: 0,
  class: "flex flex-col gap-1"
}
const _hoisted_21 = ["href"]
const _hoisted_22 = {
  key: 1,
  class: "text-slate-400"
}
const _hoisted_23 = { class: "px-4 py-3" }
const _hoisted_24 = ["title"]
const _hoisted_25 = {
  key: 1,
  class: "text-slate-400"
}
const _hoisted_26 = { class: "flex flex-col items-center justify-between gap-2 border-t border-slate-100 px-6 py-4 sm:flex-row" }
const _hoisted_27 = { class: "text-xs text-slate-500" }
const _hoisted_28 = { class: "inline-flex" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = ["disabled"]

import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import AppSelect from '@/components/input/AppSelect.vue'
import { GET_DONASI_TALLY, type DonasiTallySubmission } from '@/store/donasiTally.module'


export default /*@__PURE__*/_defineComponent({
  __name: 'DonasiTallySection',
  setup(__props) {

const store = useStore()

const page = ref(1)
const limit = ref(10)
const search = ref('')
const sortOrder = ref('DESC')

const limitOptions = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
]
const sortOptions = [
  { value: 'DESC', label: 'Terbaru' },
  { value: 'ASC', label: 'Terlama' },
]

const rows = computed<DonasiTallySubmission[]>(() => store.getters['donasiTally/list'] ?? [])
const pagination = computed(() => store.getters['donasiTally/pagination'])
const loading = computed<boolean>(() => store.getters['donasiTally/loading'])
const error = computed<string | null>(() => store.getters['donasiTally/error'])

const normalizeLabel = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

// Label pertanyaan di form Tally bisa berubah ejaan/kapitalisasi dan ada yang
// kembar ("Upload Bukti Bayar" ×3, "Tanda Terima" ×3), jadi kolom dicari
// berdasarkan pola label, bukan nama persis.
const answersMatching = (row: DonasiTallySubmission, pattern: RegExp) =>
  Object.entries(row.payload?.answersByLabel ?? {})
    .filter(([label, value]) => pattern.test(normalizeLabel(label)) && value != null && String(value).trim() !== '')
    .map(([, value]) => String(value).trim())

const answer = (row: DonasiTallySubmission, pattern: RegExp) => answersMatching(row, pattern)[0] ?? ''

const proofUrls = (row: DonasiTallySubmission) =>
  answersMatching(row, /bukti bayar|bukti transfer/)
    .flatMap((value) => value.split(/,\s*(?=https?:\/\/)/))
    .filter((value) => /^https?:\/\//.test(value))

const wantsReceipt = (row: DonasiTallySubmission) => /whatsapp|wa\b/i.test(answer(row, /tanda terima/))

const formatDateTime = (iso?: string) => {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// Nominal diketik bebas oleh donatur ("100.000", "Rp 50rb", ...): tampilkan
// sebagai Rupiah hanya bila jelas angka, selain itu apa adanya.
const formatNominal = (raw: string) => {
  if (!raw) return '-'
  // "100000", "100.000", "Rp 100.000", "Rp100.000,00" → angka; "50rb", "seikhlasnya" → apa adanya.
  const match = raw.trim().match(/^(?:rp\.?\s*)?(\d[\d.\s]*)(?:,\d{1,2})?$/i)
  if (!match) return raw
  return `Rp ${Number(match[1].replace(/\D/g, '')).toLocaleString('id-ID')}`
}

const getData = () =>
  store.dispatch(`donasiTally/${GET_DONASI_TALLY}`, {
    search: search.value || undefined,
    limit: limit.value,
    page: page.value,
    sortOrder: sortOrder.value,
  })

const reload = () => {
  page.value = 1
  getData()
}

const goTo = (target: number) => {
  page.value = target
  getData()
}

let searchTimer: ReturnType<typeof setTimeout> | null = null
const onSearchInput = () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(reload, 400)
}

onMounted(getData)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" }, [
      _createTextVNode(" Konfirmasi transfer yang dikirim donatur lewat form donasi di website. Data ini "),
      _createElementVNode("span", { class: "font-semibold" }, "belum diverifikasi"),
      _createTextVNode(" — cocokkan dengan mutasi rekening, lalu catat di tab "),
      _createElementVNode("span", { class: "font-semibold" }, "Catatan Donasi"),
      _createTextVNode(". ")
    ], -1)),
    _createElementVNode("section", _hoisted_2, [
      _createVNode(AppSelect, {
        modelValue: limit.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((limit).value = $event)),
        options: limitOptions,
        class: "w-24",
        onChange: reload
      }, null, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_3, [
        _cache[5] || (_cache[5] = _createElementVNode("svg", {
          class: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor",
          "stroke-width": "2"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            d: "M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          })
        ], -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
          placeholder: "Cari nama, email, nomor...",
          class: "w-60 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-700 placeholder-slate-400 transition-all focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30",
          onInput: onSearchInput
        }, null, 544), [
          [_vModelText, search.value]
        ])
      ]),
      _createVNode(AppSelect, {
        modelValue: sortOrder.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((sortOrder).value = $event)),
        options: sortOptions,
        class: "w-32",
        onChange: reload
      }, null, 8, ["modelValue"])
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Gagal memuat konfirmasi transfer: " + _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("table", _hoisted_7, [
          _cache[9] || (_cache[9] = _createElementVNode("thead", null, [
            _createElementVNode("tr", { class: "bg-blue-900" }, [
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "No"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Dikirim"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Donatur"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Jenis Kontribusi"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Fakultas / Sekolah"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-right text-sm font-semibold text-blue-100" }, "Nominal"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Bukti Bayar"),
              _createElementVNode("th", { class: "px-4 py-3.5 text-left text-sm font-semibold text-blue-100" }, "Tanda Terima")
            ])
          ], -1)),
          _createElementVNode("tbody", _hoisted_8, [
            (loading.value)
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(Math.min(limit.value, 5), (i) => {
                  return (_openBlock(), _createElementBlock("tr", { key: i }, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(8, (c) => {
                      return _createElementVNode("td", {
                        key: c,
                        class: "px-4 py-4"
                      }, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("div", { class: "h-4 w-full max-w-[120px] animate-pulse rounded bg-slate-100" }, null, -1)
                      ]))])
                    }), 64))
                  ]))
                }), 128))
              : (!rows.value.length)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_9, [...(_cache[7] || (_cache[7] = [
                    _createElementVNode("td", {
                      colspan: "8",
                      class: "px-4 py-12 text-center text-sm italic text-slate-400"
                    }, " Belum ada konfirmasi transfer. ", -1)
                  ]))]))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(rows.value, (row, idx) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: row.id,
                      class: "align-top transition-colors hover:bg-blue-50/40"
                    }, [
                      _createElementVNode("td", _hoisted_10, _toDisplayString(pagination.value.start + idx), 1),
                      _createElementVNode("td", _hoisted_11, _toDisplayString(formatDateTime(row.submittedAt)), 1),
                      _createElementVNode("td", _hoisted_12, [
                        _createElementVNode("p", _hoisted_13, _toDisplayString(answer(row, /^nama$/) || '-'), 1),
                        (row.extractedWhatsapp || answer(row, /(no|nomor) (hp|wa)|whatsapp/))
                          ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(row.extractedWhatsapp || answer(row, /(no|nomor) (hp|wa)|whatsapp/)), 1))
                          : _createCommentVNode("", true),
                        (answer(row, /^e ?mail$/))
                          ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(answer(row, /^e ?mail$/)), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("td", _hoisted_16, _toDisplayString(answer(row, /jenis kontribusi/) || '-'), 1),
                      _createElementVNode("td", _hoisted_17, _toDisplayString(answer(row, /fakultas|sekolah/) || '-'), 1),
                      _createElementVNode("td", _hoisted_18, _toDisplayString(formatNominal(answer(row, /nominal/))), 1),
                      _createElementVNode("td", _hoisted_19, [
                        (proofUrls(row).length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(proofUrls(row), (url, i) => {
                                return (_openBlock(), _createElementBlock("a", {
                                  key: url,
                                  href: url,
                                  target: "_blank",
                                  rel: "noopener noreferrer",
                                  class: "inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
                                }, [
                                  _createTextVNode(" Lihat" + _toDisplayString(proofUrls(row).length > 1 ? ` ${i + 1}` : '') + " ", 1),
                                  _cache[8] || (_cache[8] = _createElementVNode("svg", {
                                    class: "h-3 w-3 flex-shrink-0",
                                    fill: "none",
                                    viewBox: "0 0 24 24",
                                    stroke: "currentColor",
                                    "stroke-width": "2.5"
                                  }, [
                                    _createElementVNode("path", {
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round",
                                      d: "M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    })
                                  ], -1))
                                ], 8, _hoisted_21))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_22, "—"))
                      ]),
                      _createElementVNode("td", _hoisted_23, [
                        (wantsReceipt(row))
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: "inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700",
                              title: answer(row, /tanda terima/) || ''
                            }, " Minta via WA ", 8, _hoisted_24))
                          : (_openBlock(), _createElementBlock("span", _hoisted_25, "—"))
                      ])
                    ]))
                  }), 128))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_26, [
        _createElementVNode("span", _hoisted_27, " Menampilkan " + _toDisplayString(pagination.value.start || 0) + "–" + _toDisplayString(pagination.value.end || 0) + " dari " + _toDisplayString(pagination.value.totalEntries || 0) + " entri ", 1),
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("button", {
            class: "rounded-l-lg border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40",
            disabled: pagination.value.currentPage <= 1,
            onClick: _cache[3] || (_cache[3] = ($event: any) => (goTo(pagination.value.currentPage - 1)))
          }, "Sebelumnya", 8, _hoisted_29),
          _createElementVNode("button", {
            class: "rounded-r-lg border-y border-r border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40",
            disabled: pagination.value.currentPage >= pagination.value.totalPages,
            onClick: _cache[4] || (_cache[4] = ($event: any) => (goTo(pagination.value.currentPage + 1)))
          }, "Berikutnya", 8, _hoisted_30)
        ])
      ])
    ])
  ]))
}
}

})